import { useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Wind, Volume2, VolumeX, Info, X } from 'lucide-react';
import ChallengeCircle from './ChallengeCircle';

const ChallengeModeContent = ({
  // Challenge state
  challengeType,
  setChallengeType,
  challengeActive,
  inhaleTime,
  boltPreparationPhase,
  boltPreparationProgress,
  showMBTInput,
  setShowMBTInput,
  mbtSteps,
  setMbtSteps,
  
  // Best scores
  bestInhaleTime,
  bestExhaleTime,
  bestHoldTime,
  bestApneaTime,
  bestMBTSteps,
  
  // Last session scores
  lastInhaleScore,
  lastExhaleScore,
  lastHoldScore,
  lastApneaScore,
  lastMBTScore,
  
  // Sound
  soundEnabled,
  setSoundEnabled,
  
  // Instructions popup
  showInstructionsPopup,
  setShowInstructionsPopup,
  
  // Callbacks
  onChallengeTypeChange,
  onBackToGuided,
  boltOnly = false,
  startChallenge,
  stopChallenge,
  handleMBTSubmit,
  scrollToInstructions,
  instructionsRef
}) => {
  
  // Helper to get background gradient based on challenge type
  const getChallengeGradient = (type, opacity = 0.3) => {
    const gradients = {
      inhale: `linear-gradient(135deg, rgba(251,191,36,${opacity}) 0%, rgba(180,83,9,${opacity + 0.1}) 100%)`,
      exhale: `linear-gradient(135deg, rgba(56,189,248,${opacity}) 0%, rgba(3,105,161,${opacity + 0.1}) 100%)`,
      apnea: `linear-gradient(135deg, rgba(16,185,129,${opacity}) 0%, rgba(5,150,105,${opacity + 0.1}) 100%)`,
      mbt: `linear-gradient(135deg, rgba(251,113,133,${opacity}) 0%, rgba(190,18,60,${opacity + 0.1}) 100%)`,
      hold: `linear-gradient(135deg, rgba(167,139,250,${opacity}) 0%, rgba(91,33,182,${opacity + 0.1}) 100%)`
    };
    return gradients[type] || gradients.hold;
  };

  // Get button colors based on challenge type
  const getChallengeButtonClass = (type, isActive) => {
    const baseClasses = {
      inhale: isActive ? "bg-amber-500" : "bg-amber-400",
      exhale: isActive ? "bg-sky-500" : "bg-sky-400",
      apnea: isActive ? "bg-emerald-500" : "bg-emerald-400",
      hold: isActive ? "bg-violet-500" : "bg-violet-400",
      mbt: "bg-rose-400 hover:bg-rose-500"
    };
    return baseClasses[type] || baseClasses.hold;
  };

  // Get best time based on challenge type
  const getBestTime = () => {
    switch (challengeType) {
      case 'inhale': return bestInhaleTime;
      case 'exhale': return bestExhaleTime;
      case 'apnea': return bestApneaTime;
      case 'mbt': return bestMBTSteps;
      default: return bestHoldTime;
    }
  };

  // Get last score based on challenge type
  const getLastScore = () => {
    switch (challengeType) {
      case 'inhale': return lastInhaleScore;
      case 'exhale': return lastExhaleScore;
      case 'apnea': return lastApneaScore;
      case 'mbt': return lastMBTScore;
      default: return lastHoldScore;
    }
  };

  // Check if we have a last score to display
  const hasLastScore = () => {
    return getLastScore() !== null;
  };

  // Get the value to display in the circle
  const getCircleValue = () => {
    if (challengeType === 'mbt') {
      return 'MBT';
    }
    if (challengeType === 'hold') {
      if (boltPreparationPhase === 'inhale') return 'Inspirez';
      if (boltPreparationPhase === 'exhale') return 'Expirez';
      if (boltPreparationPhase === 'pause') return 'Pincez le nez';
      if (boltPreparationPhase === 'apnee') return 'Apnée';
      return 'BOLT';
    }
    if (challengeType === 'apnea') {
      if (challengeActive) {
        return inhaleTime.toFixed(1);
      }
      return 'Apnée';
    }
    if (challengeActive) {
      return inhaleTime.toFixed(1);
    }
    return challengeType === 'inhale' ? 'Inspirez' : 'Expirez';
  };

  // Get sublabel for the circle
  const getCircleSublabel = () => {
    if (challengeType === 'mbt') return 'Maximum Breathlessness Test';
    if (challengeType === 'hold') {
      if (boltPreparationPhase === 'apnee') return 'Retenez votre souffle';
      if (boltPreparationPhase) return '';
      return 'Body Oxygen Level Test';
    }
    if (challengeType === 'apnea') {
      return challengeActive ? 'secondes' : 'Apnée inspiratoire';
    }
    return challengeActive ? 'secondes' : 'maintenez';
  };

  // Calculate scale for animation
  const getCircleScale = () => {
    if (!challengeActive) return 1;
    if (challengeType === 'inhale') {
      return 1 + (inhaleTime / 20) * 0.5;
    }
    if (challengeType === 'exhale') {
      return 1.5 - (inhaleTime / 30) * 0.3;
    }
    if (challengeType === 'apnea') {
      // Slight pulse effect for apnea
      return 1.3 + Math.sin(inhaleTime * 0.5) * 0.05;
    }
    if (challengeType === 'hold' && boltPreparationPhase === 'inhale') {
      return 1 + boltPreparationProgress * 0.5;
    }
    if (challengeType === 'hold' && boltPreparationPhase === 'exhale') {
      return 1.5 - boltPreparationProgress * 0.5;
    }
    return 1;
  };

  // Unified press-and-hold handlers for the challenge button.
  //
  // Previously the button used onMouseDown + onMouseUp + onMouseLeave, which
  // caused "misfires" on desktop: any tiny mouse movement that slid the cursor
  // off the button fired onMouseLeave → stopChallenge, cancelling the chrono
  // mid-measurement. Pointer Events + setPointerCapture pin the pointer to
  // the element, so the challenge keeps running even if the cursor drifts.
  // Also filters right/middle clicks (e.button !== 0) which were silently
  // triggering starts before.
  const pressHandlers = {
    onPointerDown: (e) => {
      if (e.button !== undefined && e.button !== 0) return; // left click / primary touch only
      e.preventDefault();
      try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* ignore */ }
      startChallenge();
    },
    onPointerUp: (e) => {
      try { e.currentTarget.releasePointerCapture?.(e.pointerId); } catch { /* ignore */ }
      if (challengeActive) stopChallenge();
    },
    onPointerCancel: () => { if (challengeActive) stopChallenge(); },
    onContextMenu: (e) => e.preventDefault(), // prevent long-press menu on touch
  };

  return (
    <>
      {/* Mobile: Challenge Type Toggle at top. Order prioritises the
          clinically-important tests (BOLT first, MBT second) and gives
          BOLT a prominent badge — this is the mandatory weekly measure
          prescribed in every patient program, so it must never be lost
          in a row of five identical pills. Hidden when `boltOnly` is set
          (dedicated /breathing/bolt page) to prevent elderly patients
          from switching away by mistake. */}
      {!boltOnly && (
      <div className="sm:hidden mb-4">
        <div className="flex justify-center">
          <div className="inline-flex rounded-full bg-white/80 backdrop-blur p-1 border border-white/40 shadow-sm">
            <button
              onClick={() => onChallengeTypeChange('hold')}
              className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
                challengeType === 'hold'
                  ? 'bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white shadow-md'
                  : 'text-violet-600 bg-violet-50'
              }`}
              data-testid="challenge-hold-mobile"
            >
              ★ BOLT
            </button>
            <button
              onClick={() => onChallengeTypeChange('mbt')}
              className={`px-2.5 py-1.5 rounded-full text-xs font-medium transition-colors ${
                challengeType === 'mbt' ? 'bg-rose-400 text-white' : 'text-slate-600'
              }`}
            >
              MBT
            </button>
            <button
              onClick={() => onChallengeTypeChange('inhale')}
              className={`px-2 py-1.5 rounded-full text-xs font-medium transition-colors ${
                challengeType === 'inhale' ? 'bg-amber-400 text-white' : 'text-slate-600'
              }`}
            >
              Inspir
            </button>
            <button
              onClick={() => onChallengeTypeChange('exhale')}
              className={`px-2 py-1.5 rounded-full text-xs font-medium transition-colors ${
                challengeType === 'exhale' ? 'bg-sky-400 text-white' : 'text-slate-600'
              }`}
            >
              Expir
            </button>
            <button
              onClick={() => onChallengeTypeChange('apnea')}
              className={`px-2 py-1.5 rounded-full text-xs font-medium transition-colors ${
                challengeType === 'apnea' ? 'bg-emerald-400 text-white' : 'text-slate-600'
              }`}
            >
              Apnée
            </button>
          </div>
        </div>
        {/* Back to Guided button on mobile */}
        <div className="flex justify-center mt-2">
          <button
            onClick={onBackToGuided}
            className="text-white/70 text-xs underline"
          >
            ← Retour aux exercices guidés
          </button>
        </div>
      </div>
      )}

      {/* Challenge Layout: Circle - Scores */}
      <div className="flex justify-center mb-4 sm:mb-8">
        <div className="flex flex-col lg:flex-row items-center justify-center gap-4 lg:gap-12 xl:gap-20" data-testid="challenge-circle">

          {/* Instructions Button (Left) - Desktop: scroll, Mobile: popup */}
          {!challengeActive && (
            <div className="hidden lg:flex lg:w-48 shrink-0 justify-center order-1">
              <button
                onClick={scrollToInstructions}
                className="flex items-center gap-2 px-4 py-3 rounded-2xl border backdrop-blur-xl border-white/20 transition-all duration-300 hover:scale-105"
                style={{
                  background: getChallengeGradient(challengeType),
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
                }}
              >
                <Info size={18} className="text-white" />
                <span className="text-white font-medium text-sm">Instructions</span>
              </button>
            </div>
          )}
          
          {/* Empty spacer when challenge active to keep circle centered */}
          {challengeActive && (
            <div className="hidden lg:block lg:w-48 shrink-0 order-1" />
          )}

          {/* Challenge Circle */}
          <div className="order-2 lg:order-2">
            <ChallengeCircle
              challengeType={challengeType}
              challengeActive={challengeActive}
              value={getCircleValue()}
              sublabel={getCircleSublabel()}
              scale={getCircleScale()}
              boltPreparationPhase={boltPreparationPhase}
              boltPreparationProgress={boltPreparationProgress}
            />
          </div>

          {/* Best Record (Right) - Hidden on mobile, shown on desktop */}
          <div className="hidden sm:flex w-full max-w-xs sm:w-auto lg:w-48 shrink-0 justify-center order-3">
            <div 
              className="text-center p-5 rounded-3xl backdrop-blur-md shadow-2xl border border-white/20 relative overflow-hidden"
              style={{ background: getChallengeGradient(challengeType, 0.25) }}
            >
              {/* Glass shine effect */}
              <div 
                className="absolute inset-0 rounded-3xl pointer-events-none"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, transparent 50%, rgba(0,0,0,0.1) 100%)'
                }}
              />
              
              <p className="text-white font-semibold mb-2 text-base relative z-10">🏆 Meilleur</p>
              <div 
                className="text-5xl font-black text-white relative z-10"
                style={{ textShadow: '0 2px 10px rgba(0,0,0,0.3)' }}
                data-testid="best-time"
              >
                {challengeType === 'mbt' ? getBestTime() : getBestTime().toFixed(1)}
              </div>
              <p className="text-white/80 text-sm font-medium relative z-10">{challengeType === 'mbt' ? 'pas' : 'secondes'}</p>
            
              {/* Current Session Score */}
              {hasLastScore() && (
                <div className="mt-4 pt-4 border-t border-white/30 relative z-10">
                  <p className="text-white/90 font-semibold mb-2 text-sm">📊 Dernier essai</p>
                  <div 
                    className="text-3xl font-black text-white"
                    style={{ textShadow: '0 2px 10px rgba(0,0,0,0.3)' }}
                  >
                    {challengeType === 'mbt' ? getLastScore() : getLastScore()?.toFixed(1)}
                  </div>
                  <p className="text-white/70 text-xs font-medium">{challengeType === 'mbt' ? 'pas' : 'secondes'}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile: Action button right after circle */}
      <div className="sm:hidden flex flex-col items-center gap-3 mb-4">
        {challengeType === 'mbt' ? (
          showMBTInput ? (
            <div className="flex flex-col gap-3 items-center bg-white/90 backdrop-blur-sm rounded-2xl p-4 shadow-lg w-full max-w-xs">
              <Input
                type="number"
                placeholder="Nombre de pas"
                value={mbtSteps}
                onChange={(e) => setMbtSteps(e.target.value)}
                className="w-full"
                data-testid="mbt-steps-input-mobile"
              />
              <div className="flex gap-2">
                <Button
                  onClick={handleMBTSubmit}
                  className="bg-rose-500 hover:bg-rose-600 text-white"
                  data-testid="mbt-submit-mobile"
                >
                  Enregistrer
                </Button>
                <Button
                  onClick={() => {
                    setShowMBTInput(false);
                    setMbtSteps('');
                  }}
                  variant="outline"
                  className="border-rose-500 text-rose-600"
                >
                  Annuler
                </Button>
              </div>
            </div>
          ) : (
            <Button
              onClick={() => setShowMBTInput(true)}
              className="px-6 py-4 rounded-full shadow-lg bg-rose-400 hover:bg-rose-500 text-white"
            >
              <Wind className="mr-2" size={18} />
              Entrer résultat MBT
            </Button>
          )
        ) : (
          <button
            {...pressHandlers}
            className={`px-6 py-4 rounded-full shadow-lg transition-all duration-200 font-medium text-sm flex items-center justify-center select-none touch-none min-w-[280px] ${getChallengeButtonClass(challengeType, challengeActive)} text-white`}
            type="button"
            style={{ WebkitTouchCallout: 'none', WebkitUserSelect: 'none' }}
            data-testid="challenge-start-btn-mobile"
          >
            <Wind className="mr-2 pointer-events-none" size={18} />
            <span className="pointer-events-none">
              {challengeActive 
                ? (challengeType === 'inhale' ? 'Inspiration...' : challengeType === 'exhale' ? 'Expiration...' : challengeType === 'apnea' ? 'Apnée...' : 'Rétention...') 
                : (challengeType === 'inhale' ? 'Maintenez pour inspirer' : challengeType === 'exhale' ? 'Maintenez pour expirer' : challengeType === 'apnea' ? 'Maintenez en apnée' : 'Appuyer et maintenir pour Démarrer')}
            </span>
          </button>
        )}
        
        {/* Mobile: Compact score display */}
        <div className="flex gap-4 text-center">
          <div className="bg-black/40 backdrop-blur-md rounded-xl px-4 py-2 border border-white/30 shadow-lg">
            <p className="text-white text-xs font-medium">🏆 Record</p>
            <p className="text-white font-bold text-xl" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>
              {challengeType === 'mbt' ? getBestTime() : getBestTime().toFixed(1)}
              <span className="text-sm ml-1">{challengeType === 'mbt' ? 'pas' : 's'}</span>
            </p>
          </div>
          {hasLastScore() && (
            <div className="bg-black/40 backdrop-blur-md rounded-xl px-4 py-2 border border-white/30 shadow-lg">
              <p className="text-white text-xs font-medium">📊 Dernier</p>
              <p className="text-white font-bold text-xl" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>
                {challengeType === 'mbt' ? getLastScore() : getLastScore()?.toFixed(1)}
                <span className="text-sm ml-1">{challengeType === 'mbt' ? 'pas' : 's'}</span>
              </p>
            </div>
          )}
        </div>
        
        {/* Mobile: Instructions button */}
        {!challengeActive && (
          <button
            onClick={() => setShowInstructionsPopup(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-full border backdrop-blur-xl border-white/20 transition-all duration-300"
            style={{
              background: getChallengeGradient(challengeType),
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)'
            }}
          >
            <Info size={16} className="text-white" />
            <span className="text-white font-medium text-sm">Instructions</span>
          </button>
        )}
      </div>

      {/* Desktop: Challenge Type Toggle - Centered below. BOLT first and
          enlarged with a "test de référence" badge so it's the obvious
          primary action for every patient opening the page. Hidden when
          `boltOnly` is set (dedicated /breathing/bolt page). */}
      {!boltOnly && (
      <div className="hidden sm:block text-center mb-6">
        <div className="inline-flex items-center rounded-full bg-white/70 backdrop-blur p-1 border border-slate-200">
          <button
            onClick={() => onChallengeTypeChange('hold')}
            className={`px-5 py-2 rounded-full text-sm font-bold transition-colors flex items-center gap-1.5 ${
              challengeType === 'hold'
                ? 'bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white shadow-md'
                : 'text-violet-700 bg-violet-50 hover:bg-violet-100'
            }`}
            data-testid="challenge-hold"
          >
            <span className="text-base leading-none">★</span>
            BOLT
            <span className={`ml-1 text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded-full ${challengeType === 'hold' ? 'bg-white/25 text-white' : 'bg-violet-200 text-violet-800'}`}>Test de référence</span>
          </button>
          <button
            onClick={() => onChallengeTypeChange('mbt')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              challengeType === 'mbt' ? 'bg-rose-400 text-white' : 'text-slate-500 hover:text-rose-500'
            }`}
            data-testid="challenge-mbt"
          >
            MBT
          </button>
          <button
            onClick={() => onChallengeTypeChange('inhale')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              challengeType === 'inhale' ? 'bg-amber-400 text-white' : 'text-slate-500 hover:text-amber-500'
            }`}
            data-testid="challenge-inhale"
          >
            Inspiration
          </button>
          <button
            onClick={() => onChallengeTypeChange('exhale')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              challengeType === 'exhale' ? 'bg-sky-400 text-white' : 'text-slate-500 hover:text-sky-500'
            }`}
            data-testid="challenge-exhale"
          >
            Expiration
          </button>
          <button
            onClick={() => onChallengeTypeChange('apnea')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              challengeType === 'apnea' ? 'bg-emerald-400 text-white' : 'text-slate-500 hover:text-emerald-500'
            }`}
            data-testid="challenge-apnea"
          >
            Apnée
          </button>
        </div>
        {/* Short descriptor so patients know what each test measures without
            having to open the instructions popup. */}
        <p className="mt-2 text-xs text-slate-500">
          {challengeType === 'hold' && 'Rétention au repos — tolérance au CO₂ (mesure hebdomadaire)'}
          {challengeType === 'mbt' && 'Marche en apnée — mobilité du diaphragme'}
          {challengeType === 'inhale' && 'Durée d\'inspiration maximale'}
          {challengeType === 'exhale' && 'Durée d\'expiration maximale'}
          {challengeType === 'apnea' && 'Apnée après inspiration profonde'}
        </p>
      </div>
      )}

      {/* Desktop: Challenge Controls - Centered below */}
      <div className="hidden sm:flex flex-col items-center gap-4">
        {/* Main action button - centered */}
        <div className="flex justify-center">
          {challengeType === 'mbt' ? (
            showMBTInput ? (
              <div className="flex gap-3 items-center bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg">
                <Input
                  type="number"
                  placeholder="Nombre de pas"
                  value={mbtSteps}
                  onChange={(e) => setMbtSteps(e.target.value)}
                  className="w-40"
                  data-testid="mbt-steps-input"
                />
                <Button
                  onClick={handleMBTSubmit}
                  className="bg-rose-500 hover:bg-rose-600 text-white"
                  data-testid="mbt-submit"
                >
                  Enregistrer
                </Button>
                <Button
                  onClick={() => {
                    setShowMBTInput(false);
                    setMbtSteps('');
                  }}
                  variant="outline"
                  className="border-rose-500 text-rose-600"
                >
                  Annuler
                </Button>
              </div>
            ) : (
              <Button
                onClick={() => setShowMBTInput(true)}
                className="px-8 py-6 rounded-full shadow-lg bg-rose-400 hover:bg-rose-500 text-white"
                data-testid="mbt-open-input"
              >
                <Wind className="mr-2" size={20} />
                Entrer mon résultat MBT
              </Button>
            )
          ) : (
            <button
              {...pressHandlers}
              className={`px-8 py-6 rounded-full shadow-lg transition-all duration-200 font-medium text-base flex items-center justify-center select-none touch-none min-w-[260px] ${
                challengeType === 'inhale'
                  ? (challengeActive 
                      ? "bg-amber-500 hover:bg-amber-600 shadow-amber-200" 
                      : "bg-amber-400 hover:bg-amber-500 shadow-amber-100")
                  : challengeType === 'exhale'
                  ? (challengeActive 
                      ? "bg-sky-500 hover:bg-sky-600 shadow-sky-200" 
                      : "bg-sky-400 hover:bg-sky-500 shadow-sky-100")
                  : challengeType === 'apnea'
                  ? (challengeActive 
                      ? "bg-emerald-500 hover:bg-emerald-600 shadow-emerald-200" 
                      : "bg-emerald-400 hover:bg-emerald-500 shadow-emerald-100")
                  : (challengeActive 
                      ? "bg-violet-500 hover:bg-violet-600 shadow-violet-200" 
                      : "bg-violet-400 hover:bg-violet-500 shadow-violet-100")
              } text-white cursor-pointer`}
              data-testid="challenge-button"
              type="button"
              style={{ WebkitTouchCallout: 'none', WebkitUserSelect: 'none' }}
            >
              <Wind className="mr-2 pointer-events-none" size={20} />
              <span className="pointer-events-none">
                {challengeActive 
                  ? (challengeType === 'inhale' ? 'Inspiration...' : challengeType === 'exhale' ? 'Expiration...' : challengeType === 'apnea' ? 'Apnée...' : 'Rétention...') 
                  : (challengeType === 'inhale' ? 'Maintenez pour inspirer' : challengeType === 'exhale' ? 'Maintenez pour expirer' : challengeType === 'apnea' ? 'Maintenez en apnée' : 'Démarrer')}
              </span>
            </button>
          )}
        </div>
        
        {/* Sound toggle - centered below */}
        <Button
          onClick={() => setSoundEnabled(!soundEnabled)}
          variant="ghost"
          className="px-4 py-2 rounded-full"
          data-testid="sound-toggle"
        >
          {soundEnabled ? <Volume2 size={20} className="text-teal-500" /> : <VolumeX size={20} className="text-slate-400" />}
        </Button>
      </div>

      {/* Challenge tip - Hidden on mobile, shown on desktop */}
      {!challengeActive && !showMBTInput && (
        <div ref={instructionsRef}>
          {/* Instructions for Inhale/Exhale/Apnea - Hidden on mobile */}
          {(challengeType === 'inhale' || challengeType === 'exhale' || challengeType === 'apnea') && (
            <div className="hidden sm:block mt-6 p-4 rounded-2xl border backdrop-blur-xl bg-black/30 border-white/20"
              style={{ boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.2)' }}>
              <ol className="text-xs space-y-2">
                {challengeType === 'inhale' ? (
                  <>
                    <li className="text-white/90"><span className="font-bold text-white">1.</span> Expirez tout l&apos;air de vos poumons</li>
                    <li className="text-white/90"><span className="font-bold text-white">2.</span> Appuyez sur le bouton &quot;Maintenez pour inspirer&quot; et inspirez le plus doucement possible</li>
                    <li className="text-white/90"><span className="font-bold text-white">3.</span> Lorsque vos poumons sont remplis OU besoin urgent d&apos;expirer, lâchez le bouton</li>
                    <li className="text-white/90"><span className="font-bold text-white">4.</span> Observez votre record du jour</li>
                    <li className="text-white/90"><span className="font-bold text-white">5.</span> Essayez de battre votre record à chaque fois</li>
                  </>
                ) : challengeType === 'exhale' ? (
                  <>
                    <li className="text-white/90"><span className="font-bold text-white">1.</span> Inspirez jusqu&apos;à remplir vos poumons au maximum</li>
                    <li className="text-white/90"><span className="font-bold text-white">2.</span> Appuyez sur le bouton &quot;Maintenez pour expirer&quot; et expirez le plus doucement possible</li>
                    <li className="text-white/90"><span className="font-bold text-white">3.</span> Lorsque vos poumons sont vides OU besoin urgent d&apos;inspirer, lâchez le bouton</li>
                    <li className="text-white/90"><span className="font-bold text-white">4.</span> Observez votre record du jour</li>
                    <li className="text-white/90"><span className="font-bold text-white">5.</span> Essayez de battre votre record à chaque fois</li>
                  </>
                ) : (
                  <>
                    <li className="text-white/90"><span className="font-bold text-white">1.</span> Inspirez profondément pour remplir vos poumons</li>
                    <li className="text-white/90"><span className="font-bold text-white">2.</span> Appuyez sur le bouton &quot;Maintenez en apnée&quot; et retenez votre souffle</li>
                    <li className="text-white/90"><span className="font-bold text-white">3.</span> Lorsque vous ne pouvez plus tenir, lâchez le bouton</li>
                    <li className="text-white/90"><span className="font-bold text-white">4.</span> Observez votre record du jour</li>
                    <li className="text-white/90"><span className="font-bold text-white">5.</span> Essayez de battre votre record à chaque fois</li>
                  </>
                )}
              </ol>
            </div>
          )}
          
          {/* Tip for BOLT and MBT - Hidden on mobile */}
          {(challengeType === 'hold' || challengeType === 'mbt') && (
            <div className="hidden sm:block mt-8 p-4 rounded-2xl border backdrop-blur-xl bg-black/30 border-white/20"
              style={{ boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.2)' }}>
              <p className="text-sm text-center text-white/90">
                <strong className="text-white">
                  {challengeType === 'mbt' ? 'Test MBT (Maximum Breathlessness Test)' : 'Test BOLT (Body Oxygen Level Test)'} :
                </strong>{' '}
                {challengeType === 'mbt'
                  ? 'Réalisez ce test chez vous : expirez complètement, puis marchez en apnée poumons vides. Comptez le nombre de pas maximal jusqu\'au premier besoin de respirer. Entrez ensuite votre résultat.'
                  : 'Cliquez et maintenez appuyé jusqu\'à la fin de l\'exercice. Suivez les instructions à l\'écran. Tenez l\'apnée jusqu\'au PREMIER besoin de respirer (sensation confortable, pas maximum).'}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Mobile Instructions Popup */}
      {showInstructionsPopup && (
        <div 
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          onClick={() => setShowInstructionsPopup(false)}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          
          {/* Popup Content */}
          <div 
            className="relative w-full max-w-sm p-5 rounded-2xl border border-white/20"
            style={{
              background: getChallengeGradient(challengeType, 0.9),
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.4)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setShowInstructionsPopup(false)}
              className="absolute top-3 right-3 p-1.5 bg-white/20 hover:bg-white/30 rounded-full transition-colors"
            >
              <X size={18} className="text-white" />
            </button>
            
            {/* Title */}
            <h3 className="text-white font-bold text-lg mb-4 pr-8">
              Instructions {challengeType === 'inhale' ? 'Inspiration' : challengeType === 'exhale' ? 'Expiration' : challengeType === 'apnea' ? 'Apnée' : challengeType === 'mbt' ? 'MBT' : 'BOLT'}
            </h3>
            
            {/* Instructions content */}
            <ol className="text-sm space-y-2.5">
              {challengeType === 'inhale' ? (
                <>
                  <li className="text-white/90"><span className="font-bold text-white">1.</span> Expirez tout l&apos;air de vos poumons</li>
                  <li className="text-white/90"><span className="font-bold text-white">2.</span> Appuyez sur le bouton et inspirez le plus doucement possible</li>
                  <li className="text-white/90"><span className="font-bold text-white">3.</span> Lorsque vos poumons sont remplis, lâchez le bouton</li>
                  <li className="text-white/90"><span className="font-bold text-white">4.</span> Observez votre record du jour</li>
                  <li className="text-white/90"><span className="font-bold text-white">5.</span> Essayez de battre votre record !</li>
                </>
              ) : challengeType === 'exhale' ? (
                <>
                  <li className="text-white/90"><span className="font-bold text-white">1.</span> Inspirez jusqu&apos;à remplir vos poumons au maximum</li>
                  <li className="text-white/90"><span className="font-bold text-white">2.</span> Appuyez sur le bouton et expirez le plus doucement possible</li>
                  <li className="text-white/90"><span className="font-bold text-white">3.</span> Lorsque vos poumons sont vides, lâchez le bouton</li>
                  <li className="text-white/90"><span className="font-bold text-white">4.</span> Observez votre record du jour</li>
                  <li className="text-white/90"><span className="font-bold text-white">5.</span> Essayez de battre votre record !</li>
                </>
              ) : challengeType === 'apnea' ? (
                <>
                  <li className="text-white/90"><span className="font-bold text-white">1.</span> Inspirez profondément</li>
                  <li className="text-white/90"><span className="font-bold text-white">2.</span> Appuyez sur le bouton et retenez votre souffle</li>
                  <li className="text-white/90"><span className="font-bold text-white">3.</span> Quand vous ne pouvez plus tenir, lâchez</li>
                  <li className="text-white/90"><span className="font-bold text-white">4.</span> Observez votre record du jour</li>
                  <li className="text-white/90"><span className="font-bold text-white">5.</span> Essayez de battre votre record !</li>
                </>
              ) : challengeType === 'hold' ? (
                <>
                  <li className="text-white/90"><span className="font-bold text-white">1.</span> Appuyez sur le bouton pour démarrer</li>
                  <li className="text-white/90"><span className="font-bold text-white">2.</span> Suivez les instructions : Inspirez → Expirez</li>
                  <li className="text-white/90"><span className="font-bold text-white">3.</span> Retenez votre souffle après l&apos;expiration</li>
                  <li className="text-white/90"><span className="font-bold text-white">4.</span> Arrêtez au premier besoin d&apos;air</li>
                  <li className="text-white/90"><span className="font-bold text-white">5.</span> Normal : 25s / Athlète : 40s</li>
                </>
              ) : (
                <>
                  <li className="text-white/90"><span className="font-bold text-white">1.</span> Expirez normalement</li>
                  <li className="text-white/90"><span className="font-bold text-white">2.</span> Pincez votre nez</li>
                  <li className="text-white/90"><span className="font-bold text-white">3.</span> Marchez à un rythme normal</li>
                  <li className="text-white/90"><span className="font-bold text-white">4.</span> Comptez vos pas</li>
                  <li className="text-white/90"><span className="font-bold text-white">5.</span> Arrêtez au premier besoin d&apos;air</li>
                </>
              )}
            </ol>
          </div>
        </div>
      )}
    </>
  );
};

export default ChallengeModeContent;
